import { useRef } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import { CloseCircleOutline } from '@aslanpay/portal-common-lib';

import { useCreateCompanyStore } from 'app/store';

import Step from 'app/components/UI/Step';
import useStep from 'app/hooks/useStep';

import { CompanyDetailsForm, CompanyModulesForm } from '../CreateCompanyForm';

import { CreateCompanyModalProps } from './types';

function CreateCompanyModal({ isOpen, onClose, onBack }: CreateCompanyModalProps) {
  const resetCompanyData = useCreateCompanyStore((state) => state.resetCompanyData);

  const submitButtonRef = useRef<HTMLElement>(null);

  const steps = [
    {
      id: 'company-define-company-form',
      name: 'Company details',
    },
    {
      id: 'company-modules-form',
      name: 'Select service(s)',
    },
  ];

  const { currentStep, nextStep, previousStep, canPrevStep, resetSteps } = useStep(steps.length);

  const handleClose = () => {
    resetCompanyData();
    resetSteps();
    onClose();
  };

  // On the first step there's nowhere internal to go back to - hand off to the parent's onBack (returning to
  // the create-choice modal) instead, resetting the same way handleClose does.
  const handleBackToChoice = () => {
    resetCompanyData();
    resetSteps();
    onBack?.();
  };

  const renderCurrentStepForm = () => {
    switch (steps[currentStep].id) {
      case 'company-define-company-form':
        return (
          <CompanyDetailsForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={nextStep}
            previousStep={onBack ? handleBackToChoice : previousStep}
            canPrevStep={onBack ? true : canPrevStep}
          />
        );

      case 'company-modules-form':
        return (
          <CompanyModulesForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={handleClose}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      default:
        return (
          <CompanyDetailsForm
            id="company-details-form"
            ref={submitButtonRef}
            onStepAction={handleClose}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Create a new company</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close create company"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing="40px">
            <HStack spacing="0" flexGrow={1} alignItems="flex-start">
              {steps.map((step, id) => (
                <Step
                  key={step.name}
                  number={id + 1}
                  name={step.name}
                  isActive={currentStep === id}
                  isCompleted={currentStep > id}
                  isFirstStep={id === 0}
                  isLastStep={steps.length === id + 1}
                />
              ))}
            </HStack>
            {renderCurrentStepForm()}
          </Stack>
        </ModalBody>
        <ModalFooter ref={submitButtonRef}>
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreateCompanyModal;
